// Catalog.ID Client — Module Identity Registry
// ============================================================================
//
// Stores Ed25519 public keys for dashboard modules that have called the
// register-module entrypoint. Persisted in localStorage so registrations
// survive tab reloads. Scope is per-cid (different identities should not
// see each other's modules) but keyed independently from grant policies.
//
// First-writer-wins per (cid, moduleId): the first module to register a
// pubkey owns that moduleId until the user explicitly clears it from the
// Connected Modules pane. This is consistent with the roadmap §6.1 item 5
// and prevents naive race-induced impersonation.
//
// RESERVED IDS (cid-encrypted-storage §4.1): `cid` and `shared` are refused
// outright, by `tryRegister` AND by `override`. Downstream the registered id
// IS the derivation namespace and the account-blob prefix, so a module holding
// `cid` would derive the vault's own keys and read the vault's own documents.
// The refusal lives here, before any consent dialog, because a dialog asking
// the user to approve "cid" would be asking about something no user can judge.
// ============================================================================

import { isReservedModuleId } from '../crypto/derive';

const STORAGE_PREFIX = 'cid_module_pubkeys_v1_';

export interface RegisteredModule {
    moduleId: string;
    publicKeyB64: string;     // raw 32-byte Ed25519 pubkey, base64
    registeredAt: string;     // ISO-8601
    label?: string;           // optional human-readable name
}

function storageKey(cid: string): string { return `${STORAGE_PREFIX}${cid}`; }

function load(cid: string): RegisteredModule[] {
    try {
        const raw = localStorage.getItem(storageKey(cid));
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isModule);
    } catch { return []; }
}

function save(cid: string, mods: RegisteredModule[]): void {
    try { localStorage.setItem(storageKey(cid), JSON.stringify(mods)); }
    catch { /* storage quota — best effort */ }
}

function isModule(x: unknown): x is RegisteredModule {
    return typeof x === 'object' && x !== null
        && typeof (x as RegisteredModule).moduleId === 'string'
        && typeof (x as RegisteredModule).publicKeyB64 === 'string';
}

export function lookup(cid: string, moduleId: string): RegisteredModule | undefined {
    return load(cid).find(m => m.moduleId === moduleId);
}

/**
 * First-writer-wins registration. Returns:
 *   - 'registered' if newly added,
 *   - 'already' if a different (or matching) entry already exists for this moduleId,
 *   - 'reserved' if the id belongs to the vault (§4.1) and no module may hold it.
 *
 * The caller (Key Grant request handler) responds to the requester so they
 * know whether the registration took.
 */
export function tryRegister(cid: string, moduleId: string, publicKeyB64: string, label?: string): 'registered' | 'already' | 'reserved' {
    if (isReservedModuleId(moduleId)) return 'reserved';
    const existing = load(cid);
    const dup = existing.find(m => m.moduleId === moduleId);
    if (dup) return 'already';
    existing.push({
        moduleId,
        publicKeyB64,
        registeredAt: new Date().toISOString(),
        label,
    });
    save(cid, existing);
    return 'registered';
}

/**
 * Forcibly replace a registered module's pubkey. Triggered from the Connected
 * Modules pane when the user explicitly accepts a re-registration. The caller
 * is responsible for confirming with the user before invoking this.
 */
export function override(cid: string, moduleId: string, publicKeyB64: string, label?: string): void {
    // A reserved id is refused here too: this is the "accept re-registration"
    // button, and an id no module may hold in the first place must not become
    // holdable by way of the override path.
    if (isReservedModuleId(moduleId)) return;
    const filtered = load(cid).filter(m => m.moduleId !== moduleId);
    filtered.push({
        moduleId,
        publicKeyB64,
        registeredAt: new Date().toISOString(),
        label,
    });
    save(cid, filtered);
}

export function unregister(cid: string, moduleId: string): void {
    save(cid, load(cid).filter(m => m.moduleId !== moduleId));
}

export function listAll(cid: string): RegisteredModule[] {
    return load(cid);
}

/**
 * Forget every module registration for one identity. Used by "Forget this
 * device", which leaves nothing identity-specific on this browser; an ordinary
 * logout keeps registrations.
 */
export function clearAllModules(cid: string): void {
    try { localStorage.removeItem(storageKey(cid)); }
    catch { /* storage disabled — nothing persisted to remove */ }
}
